/**
 * R18-4 / R18-7.1 — Crowd Avatar Tile.
 *
 * Renders as an avatar by default (initial + deterministic hue + name +
 * role badge). Driven by R18-7.1 architecture decision: NO audience-facing
 * tile ever subscribes to live video unless it's pinned. Even publishers
 * get an avatar tile here, but with a small green "On cam" LED so the
 * audience knows the user is broadcasting.
 *
 * HOST/COHOST HOVER PREVIEW (R18-7.1 Part B2)
 * -------------------------------------------
 * If `canPreview && isPublishing`, mouse-enter starts a 200ms intent
 * timer. After 200ms we flip into "preview" mode and call
 * `renderPreview()` (which is expected to return a live <CamTile> — the
 * CamTile's IntersectionObserver subscribes the video on mount). A 5s
 * timer auto-flips back to avatar; mouse-leave cancels everything. The
 * unmount of the CamTile then triggers its own subscribeIntent(...,
 * 'video', false) call, restoring the leak-free state.
 *
 * R18-8 — When `reconnecting=true` (a pinned identity has gone offline but
 * we're holding the slot), the avatar greys out and shows a spinner.
 *
 * Testids:
 *   - crowd-avatar-tile-{identity}
 *   - crowd-avatar-reconnecting-{identity}    (when reconnecting)
 *   - crowd-avatar-oncam-{identity}            (green LED when isPublishing)
 *   - crowd-avatar-preview-{identity}          (PREVIEW badge during hover)
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";

function hashHue(str) {
  let h = 0;
  for (let i = 0; i < (str || "").length; i++) h = (h * 31 + str.charCodeAt(i)) | 0;
  return Math.abs(h) % 360;
}

const HOVER_INTENT_MS = 200;
const PREVIEW_TIMEOUT_MS = 5000;

export default function CrowdAvatarTile({
  identity, displayName, role = "audience", isDj = false, reconnecting = false,
  // R18-7.1 Part B1 — show a tiny "On cam" green LED if this member is
  // currently publishing camera (we still don't subscribe their video).
  isPublishing = false,
  // R18-7.1 Part B2 — host/cohost-only hover preview. `renderPreview()`
  // should return a node that mounts a live CamTile for `identity`; mounting
  // it triggers the existing IO-driven subscribeIntent. Unmounting cancels.
  canPreview = false, renderPreview,
}) {
  const initial = (displayName || identity || "?").trim().slice(0, 1).toUpperCase();
  const hue = hashHue(identity || displayName);
  const ringClass = isDj
    ? "ring-[#FF007F]/65"
    : role === "host"
      ? "ring-amber-400/55"
      : role === "cohost"
        ? "ring-[#00F0FF]/55"
        : "ring-white/10";

  const [previewing, setPreviewing] = useState(false);
  const intentTimerRef = useRef(null);
  const previewTimerRef = useRef(null);

  const clearTimers = useCallback(() => {
    if (intentTimerRef.current) {
      clearTimeout(intentTimerRef.current);
      intentTimerRef.current = null;
    }
    if (previewTimerRef.current) {
      clearTimeout(previewTimerRef.current);
      previewTimerRef.current = null;
    }
  }, []);

  const stopPreview = useCallback((reason) => {
    clearTimers();
    setPreviewing((wasPreview) => {
      if (wasPreview) {
        // eslint-disable-next-line no-console
        console.log(`[R18-7.1] hover-preview end=${identity} reason=${reason || "unknown"}`);
      }
      return false;
    });
  }, [clearTimers, identity]);

  const onEnter = useCallback(() => {
    if (!canPreview || !isPublishing || reconnecting || !renderPreview) return;
    clearTimers();
    intentTimerRef.current = setTimeout(() => {
      // eslint-disable-next-line no-console
      console.log(`[R18-7.1] hover-preview start=${identity}`);
      setPreviewing(true);
      previewTimerRef.current = setTimeout(() => stopPreview("timeout"), PREVIEW_TIMEOUT_MS);
    }, HOVER_INTENT_MS);
  }, [canPreview, isPublishing, reconnecting, renderPreview, identity, clearTimers, stopPreview]);

  const onLeave = useCallback(() => {
    // If preview hasn't started yet (intent timer still pending), just cancel.
    if (intentTimerRef.current && !previewing) {
      clearTimeout(intentTimerRef.current);
      intentTimerRef.current = null;
      return;
    }
    if (previewing) stopPreview("mouseout");
  }, [previewing, stopPreview]);

  useEffect(() => () => clearTimers(), [clearTimers]);

  if (previewing && typeof renderPreview === "function") {
    // The CamTile mounted by renderPreview handles the live subscription
    // via its own IntersectionObserver. We just overlay a PREVIEW badge
    // and listen for mouse-leave to unmount.
    return (
      <div
        className="relative"
        onMouseLeave={onLeave}
        data-testid={`crowd-avatar-preview-${identity}`}
      >
        {renderPreview()}
        <div className="absolute top-1 right-1 px-1.5 py-0.5 rounded bg-amber-500/90 text-[8px] font-black tracking-[0.14em] text-black pointer-events-none">
          PREVIEW
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid={`crowd-avatar-tile-${identity}`}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      className={`relative aspect-[4/3] rounded-md overflow-hidden ring-1 ${ringClass} bg-[#0c0c14] flex flex-col items-center justify-center text-center select-none ${
        reconnecting ? "opacity-65" : ""
      } ${canPreview && isPublishing && !reconnecting ? "cursor-zoom-in" : ""}`}
    >
      <div
        className="w-14 h-14 rounded-full grid place-items-center text-xl font-bold"
        style={{
          background: `radial-gradient(circle at 30% 25%, hsla(${hue},80%,55%,0.95), hsla(${hue},70%,30%,1))`,
          color: "white",
          textShadow: "0 1px 2px rgba(0,0,0,0.5)",
        }}
      >
        {initial}
      </div>
      <div className="mt-2 text-[11px] font-semibold text-white/90 truncate max-w-[80%]" title={displayName}>
        {displayName || "Member"}
      </div>
      <div className="absolute top-1 left-1 flex items-center gap-1">
        {isDj && (
          <span className="px-1.5 py-0.5 rounded bg-[#FF007F]/85 text-[8px] font-black tracking-[0.12em] text-white">
            DJ
          </span>
        )}
        {!isDj && role === "host" && (
          <span className="px-1.5 py-0.5 rounded bg-amber-500/85 text-[8px] font-black tracking-[0.12em] text-black">
            HOST
          </span>
        )}
        {!isDj && role === "cohost" && (
          <span className="px-1.5 py-0.5 rounded bg-[#00F0FF]/85 text-[8px] font-black tracking-[0.12em] text-black">
            COHOST
          </span>
        )}
      </div>
      {/* R18-7.1 — Tiny green LED indicating "On cam" without subscribing video. */}
      {isPublishing && !reconnecting && (
        <div
          className="absolute top-1 right-1 flex items-center gap-1 text-[8px] uppercase tracking-[0.12em] text-emerald-200/85 font-bold"
          data-testid={`crowd-avatar-oncam-${identity}`}
          title="Publishing camera (pin to view)"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.85)] animate-pulse" />
          On cam
        </div>
      )}
      {reconnecting && (
        <div
          className="absolute inset-x-0 bottom-1 flex items-center justify-center gap-1.5 text-[10px] text-white/75 bg-black/40 mx-2 rounded py-0.5"
          data-testid={`crowd-avatar-reconnecting-${identity}`}
        >
          <Loader2 className="w-3 h-3 animate-spin" />
          Reconnecting…
        </div>
      )}
    </div>
  );
}
